import { useEffect, useState } from "react";
import { Plus, Scales, Prohibit, Gift, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CCNav } from "../components/cc/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const CLASSES = ["all", "new_business", "renewal", "endorsement", "reinstatement"];
const CLASS_LABEL = { all: "All types", new_business: "New Business", renewal: "Renewal", endorsement: "Endorsement", reinstatement: "Reinstatement" };

function ScopeCell({ r }) {
  return (
    <span>
      <strong>{r.producer_name || "Agency default"}</strong>
      <span className="text-slate-400"> · {r.carrier_name || "all carriers"} · {CLASS_LABEL[r.txn_class]}</span>
    </span>
  );
}

function RevokeDialog({ item, kind, onClose, onDone }) {
  const [reason, setReason] = useState("");
  useEffect(() => setReason(""), [item?.id]);
  if (!item) return null;
  const revoke = async () => {
    try {
      await api.delete(`/commission/${kind}/${item.id}`, { data: { reason } });
      toast.success("Revoked — version retained, past evaluations unaffected");
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Revoke this rule?</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">The rule stops applying to future calculations. Historical evaluations keep referencing it (versioned).</p>
        <Textarea placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cc-rule-revoke-reason" />
        <Button variant="destructive" disabled={!reason.trim()} onClick={revoke} data-testid="cc-rule-revoke-confirm">Revoke</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function CommissionRulesPage() {
  const { hasPerm } = useAuth();
  const canRules = hasPerm("commission.rules");
  const [rules, setRules] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [splits, setSplits] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [producers, setProducers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [showRevoked, setShowRevoked] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addBonusOpen, setAddBonusOpen] = useState(false);
  const [addSplitOpen, setAddSplitOpen] = useState(false);
  const [revokeItem, setRevokeItem] = useState(null);
  const [revokeKind, setRevokeKind] = useState("rules");
  const emptyRule = { producer_id: "__agency__", carrier_id: "__all__", txn_class: "all", rate_type: "percent", rate_percent: "", flat_amount: "", effective_from: "", effective_to: "", note: "" };
  const emptyBonus = { name: "", amount: "", producer_id: "__all__", carrier_id: "__all__", txn_class: "new_business", effective_from: "", effective_to: "", reversal_policy: "reverse_on_full_chargeback", note: "" };
  const emptySplit = { policy_id: "", components: [{ producer_id: "", percent: "" }, { producer_id: "", percent: "" }], effective_from: "", effective_to: "", note: "", confirm_over_100: false };
  const [form, setForm] = useState(emptyRule);
  const [bform, setBform] = useState(emptyBonus);
  const [sform, setSform] = useState(emptySplit);

  const load = async () => {
    const [r, b, s, p, c, pol] = await Promise.all([
      api.get("/commission/rules"), api.get("/commission/bonus-rules"), api.get("/commission/splits"),
      api.get("/commission/producers"), api.get("/commission/carriers"), api.get("/commission/policies"),
    ]);
    setRules(r.data); setBonuses(b.data); setSplits(s.data); setProducers(p.data); setCarriers(c.data);
    setPolicies(pol.data.policies || []);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const saveRule = async () => {
    try {
      await api.post("/commission/rules", {
        producer_id: form.producer_id === "__agency__" ? null : form.producer_id,
        carrier_id: form.carrier_id === "__all__" ? null : form.carrier_id,
        txn_class: form.txn_class, rate_type: form.rate_type,
        rate_percent: form.rate_type === "percent" ? form.rate_percent : "0",
        flat_amount: form.rate_type === "flat" ? form.flat_amount : null,
        effective_from: form.effective_from, effective_to: form.effective_to || null, note: form.note,
      });
      toast.success("Rule created");
      setAddOpen(false); setForm(emptyRule); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create rule"); }
  };

  const saveBonus = async () => {
    try {
      await api.post("/commission/bonus-rules", {
        name: bform.name, amount: bform.amount,
        producer_id: bform.producer_id === "__all__" ? null : bform.producer_id,
        carrier_id: bform.carrier_id === "__all__" ? null : bform.carrier_id,
        txn_class: bform.txn_class, effective_from: bform.effective_from,
        effective_to: bform.effective_to || null, reversal_policy: bform.reversal_policy, note: bform.note,
      });
      toast.success("Bonus rule created");
      setAddBonusOpen(false); setBform(emptyBonus); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create bonus"); }
  };

  const splitTotal = sform.components.reduce((a, c) => a + (Number(c.percent) || 0), 0);
  const splitValid = sform.policy_id && sform.effective_from && sform.note.trim().length >= 5 &&
    sform.components.length >= 2 && sform.components.every((c) => c.producer_id && Number(c.percent) > 0) &&
    (splitTotal <= 100 || sform.confirm_over_100);

  const saveSplit = async () => {
    try {
      await api.post("/commission/splits", {
        policy_id: sform.policy_id,
        components: sform.components.map((c) => ({ producer_id: c.producer_id, percent: c.percent })),
        effective_from: sform.effective_from, effective_to: sform.effective_to || null,
        note: sform.note, confirm_over_100: sform.confirm_over_100,
      });
      toast.success("Split created — remainder cents reconcile deterministically");
      setAddSplitOpen(false); setSform(emptySplit); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create split"); }
  };

  const setComp = (i, patch) => {
    const components = sform.components.map((c, j) => (j === i ? { ...c, ...patch } : c));
    setSform({ ...sform, components });
  };

  const shownRules = rules.filter((r) => showRevoked || r.status === "active");
  const shownBonuses = bonuses.filter((r) => showRevoked || r.status === "active");
  const shownSplits = splits.filter((r) => showRevoked || r.status === "active");

  return (
    <div data-testid="commission-rules-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-heading font-semibold flex items-center gap-2">
            <Scales size={26} weight="duotone" className="text-indigo-600" /> Commission rules
          </h1>
          <p className="text-sm text-slate-500 mt-1">Effective-dated, versioned, deterministic. No rate exists unless configured — specificity decides precedence, equal-priority conflicts block.</p>
        </div>
        <button onClick={() => setShowRevoked(!showRevoked)} className="text-xs text-slate-500 underline" data-testid="cc-toggle-revoked">
          {showRevoked ? "Hide revoked" : "Show revoked"}
        </button>
      </div>
      <CCNav />

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden mb-5">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-medium text-sm">Rate rules (% of validated carrier-paid commission)</h2>
          {canRules && <Button size="sm" onClick={() => setAddOpen(true)} data-testid="cc-add-rule-open"><Plus size={14} className="mr-1" /> New rule</Button>}
        </div>
        {shownRules.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">No rules yet — nothing pays out until rates are configured here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cc-rules-table">
              <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                <th className="px-4 py-2">Scope (specificity decides precedence)</th>
                <th className="px-4 py-2 text-right">Rate</th>
                <th className="px-4 py-2">Effective</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Created</th>
                <th className="px-4 py-2"></th>
              </tr></thead>
              <tbody>
                {shownRules.map((r) => (
                  <tr key={r.id} className={`border-b border-slate-50 ${r.status === "revoked" ? "opacity-50" : ""}`}>
                    <td className="px-4 py-2.5"><ScopeCell r={r} />{r.note && <p className="text-xs text-slate-400">{r.note}</p>}</td>
                    <td className="px-4 py-2.5 text-right font-semibold">{r.rate_type === "flat" ? <>${r.flat_amount}<span className="text-xs font-normal text-slate-400"> flat</span></> : `${r.rate_percent}%`}</td>
                    <td className="px-4 py-2.5 text-xs">{r.effective_from} → {r.effective_to || "open"}</td>
                    <td className="px-4 py-2.5"><Badge className={r.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}>{r.status}</Badge></td>
                    <td className="px-4 py-2.5 text-xs text-slate-400">{r.created_by_name}<br />{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {canRules && r.status === "active" && (
                        <Button variant="ghost" size="sm" className="h-7 text-red-600" onClick={() => { setRevokeKind("rules"); setRevokeItem(r); }} data-testid={`cc-revoke-rule-${r.id}`}><Prohibit size={14} /></Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden mb-5">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-medium text-sm flex items-center gap-1.5"><UsersThree size={16} className="text-indigo-600" /> Splits (one carrier transaction shared across producers — % of carrier commission, remainder cents reconcile exactly)</h2>
          {canRules && <Button size="sm" variant="outline" onClick={() => setAddSplitOpen(true)} data-testid="cc-add-split-open"><Plus size={14} className="mr-1" /> New split</Button>}
        </div>
        {shownSplits.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">No splits — each policy pays its single assigned producer by rule.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cc-splits-table">
              <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                <th className="px-4 py-2">Policy</th><th className="px-4 py-2">Allocation</th>
                <th className="px-4 py-2 text-right">Total</th><th className="px-4 py-2">Effective</th>
                <th className="px-4 py-2">Status</th><th className="px-4 py-2">Created</th><th className="px-4 py-2"></th>
              </tr></thead>
              <tbody>
                {shownSplits.map((s) => (
                  <tr key={s.id} className={`border-b border-slate-50 ${s.status === "revoked" ? "opacity-50" : ""}`}>
                    <td className="px-4 py-2.5"><span className="font-mono text-xs">{s.policy_number}</span>
                      {s.insured_name && <p className="text-xs text-slate-400">{s.insured_name}</p>}</td>
                    <td className="px-4 py-2.5 text-xs">{s.components.map((c) => `${c.producer_name} ${c.percent}%`).join(" · ")}
                      {s.note && <p className="text-slate-400">{s.note}</p>}</td>
                    <td className={`px-4 py-2.5 text-right font-semibold ${Number(s.total_percent) > 100 ? "text-amber-700" : ""}`}>{s.total_percent}%</td>
                    <td className="px-4 py-2.5 text-xs">{s.effective_from} → {s.effective_to || "open"}</td>
                    <td className="px-4 py-2.5"><Badge className={s.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}>{s.status}</Badge></td>
                    <td className="px-4 py-2.5 text-xs text-slate-400">{s.created_by_name}<br />{fmtDate(s.created_at)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {canRules && s.status === "active" && (
                        <Button variant="ghost" size="sm" className="h-7 text-red-600" onClick={() => { setRevokeKind("splits"); setRevokeItem(s); }} data-testid={`cc-revoke-split-${s.id}`}><Prohibit size={14} /></Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-medium text-sm flex items-center gap-1.5"><Gift size={16} className="text-indigo-600" /> Bonus rules (fixed $ per qualifying transaction, one-time keys prevent double pay)</h2>
          {canRules && <Button size="sm" variant="outline" onClick={() => setAddBonusOpen(true)} data-testid="cc-add-bonus-open"><Plus size={14} className="mr-1" /> New bonus</Button>}
        </div>
        {shownBonuses.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">No bonus rules.</p>
        ) : (
          <table className="w-full text-sm" data-testid="cc-bonus-table">
            <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
              <th className="px-4 py-2">Bonus</th><th className="px-4 py-2 text-right">Amount</th>
              <th className="px-4 py-2">Scope</th><th className="px-4 py-2">Effective</th>
              <th className="px-4 py-2">Reversal</th><th className="px-4 py-2">Status</th><th className="px-4 py-2"></th>
            </tr></thead>
            <tbody>
              {shownBonuses.map((b) => (
                <tr key={b.id} className={`border-b border-slate-50 ${b.status === "revoked" ? "opacity-50" : ""}`}>
                  <td className="px-4 py-2.5 font-medium">{b.name}</td>
                  <td className="px-4 py-2.5 text-right font-semibold">${b.amount}</td>
                  <td className="px-4 py-2.5 text-xs">{b.producer_name || "all producers"} · {b.carrier_name || "all carriers"} · {CLASS_LABEL[b.txn_class]}</td>
                  <td className="px-4 py-2.5 text-xs">{b.effective_from} → {b.effective_to || "open"}</td>
                  <td className="px-4 py-2.5 text-xs">{b.reversal_policy === "no_reversal" ? "never reversed" : "reversed on full chargeback"}</td>
                  <td className="px-4 py-2.5"><Badge className={b.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}>{b.status}</Badge></td>
                  <td className="px-4 py-2.5 text-right">
                    {canRules && b.status === "active" && (
                      <Button variant="ghost" size="sm" className="h-7 text-red-600" onClick={() => { setRevokeKind("bonus-rules"); setRevokeItem(b); }} data-testid={`cc-revoke-bonus-${b.id}`}><Prohibit size={14} /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New commission rule</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs text-slate-500">Producer</label>
              <Select value={form.producer_id} onValueChange={(v) => setForm({ ...form, producer_id: v })}>
                <SelectTrigger data-testid="cc-rule-producer-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__agency__">Agency default (explicit)</SelectItem>
                  {producers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name_raw}{p.user_name ? ` → ${p.user_name}` : ""}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><label className="text-xs text-slate-500">Carrier</label>
              <Select value={form.carrier_id} onValueChange={(v) => setForm({ ...form, carrier_id: v })}>
                <SelectTrigger data-testid="cc-rule-carrier-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All carriers</SelectItem>
                  {carriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><label className="text-xs text-slate-500">Transaction class</label>
              <Select value={form.txn_class} onValueChange={(v) => setForm({ ...form, txn_class: v })}>
                <SelectTrigger data-testid="cc-rule-class-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{CLASS_LABEL[c]}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="text-xs text-slate-500">Rule type</label>
              <Select value={form.rate_type} onValueChange={(v) => setForm({ ...form, rate_type: v })}>
                <SelectTrigger data-testid="cc-rule-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">% of commission</SelectItem>
                  <SelectItem value="flat">Flat $ per transaction</SelectItem>
                </SelectContent>
              </Select></div>
            {form.rate_type === "flat" ? (
              <div><label className="text-xs text-slate-500">Flat $ amount</label>
                <Input placeholder="e.g. 25.00" value={form.flat_amount} onChange={(e) => setForm({ ...form, flat_amount: e.target.value })} data-testid="cc-rule-flat-input" /></div>
            ) : (
              <div><label className="text-xs text-slate-500">Rate %</label>
                <Input placeholder="e.g. 30" value={form.rate_percent} onChange={(e) => setForm({ ...form, rate_percent: e.target.value })} data-testid="cc-rule-rate-input" /></div>
            )}
            {form.rate_type === "flat" && (
              <p className="col-span-2 text-xs text-slate-400 -mt-1">Pays the producer a fixed dollar amount per matched transaction, regardless of the carrier commission. Negative transactions (chargebacks) deduct the same amount.</p>
            )}
            <div><label className="text-xs text-slate-500">Effective from</label>
              <Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="cc-rule-from-input" /></div>
            <div><label className="text-xs text-slate-500">Effective to (optional)</label>
              <Input type="date" value={form.effective_to} onChange={(e) => setForm({ ...form, effective_to: e.target.value })} data-testid="cc-rule-to-input" /></div>
            <div><label className="text-xs text-slate-500">Note</label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="cc-rule-note-input" /></div>
          </div>
          <Button onClick={saveRule} disabled={!(form.rate_type === "flat" ? form.flat_amount : form.rate_percent) || !form.effective_from} data-testid="cc-rule-save">Create rule</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={addBonusOpen} onOpenChange={setAddBonusOpen}>
        <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New bonus rule</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-500">Name</label>
              <Input placeholder="e.g. $25 NB Carrier X" value={bform.name} onChange={(e) => setBform({ ...bform, name: e.target.value })} data-testid="cc-bonus-name-input" /></div>
            <div><label className="text-xs text-slate-500">Amount $ per transaction</label>
              <Input placeholder="25.00" value={bform.amount} onChange={(e) => setBform({ ...bform, amount: e.target.value })} data-testid="cc-bonus-amount-input" /></div>
            <div><label className="text-xs text-slate-500">Producer</label>
              <Select value={bform.producer_id} onValueChange={(v) => setBform({ ...bform, producer_id: v })}>
                <SelectTrigger data-testid="cc-bonus-producer-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All producers</SelectItem>
                  {producers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name_raw}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><label className="text-xs text-slate-500">Carrier</label>
              <Select value={bform.carrier_id} onValueChange={(v) => setBform({ ...bform, carrier_id: v })}>
                <SelectTrigger data-testid="cc-bonus-carrier-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All carriers</SelectItem>
                  {carriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><label className="text-xs text-slate-500">Qualifying class</label>
              <Select value={bform.txn_class} onValueChange={(v) => setBform({ ...bform, txn_class: v })}>
                <SelectTrigger data-testid="cc-bonus-class-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{CLASS_LABEL[c]}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="text-xs text-slate-500">Reversal policy</label>
              <Select value={bform.reversal_policy} onValueChange={(v) => setBform({ ...bform, reversal_policy: v })}>
                <SelectTrigger data-testid="cc-bonus-reversal-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="reverse_on_full_chargeback">Reverse on full chargeback</SelectItem>
                  <SelectItem value="no_reversal">Never reverse</SelectItem>
                </SelectContent>
              </Select></div>
            <div><label className="text-xs text-slate-500">Effective from</label>
              <Input type="date" value={bform.effective_from} onChange={(e) => setBform({ ...bform, effective_from: e.target.value })} data-testid="cc-bonus-from-input" /></div>
            <div><label className="text-xs text-slate-500">Effective to (optional)</label>
              <Input type="date" value={bform.effective_to} onChange={(e) => setBform({ ...bform, effective_to: e.target.value })} data-testid="cc-bonus-to-input" /></div>
          </div>
          <Button onClick={saveBonus} disabled={!bform.name || !bform.amount || !bform.effective_from} data-testid="cc-bonus-save">Create bonus rule</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={addSplitOpen} onOpenChange={setAddSplitOpen}>
        <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New split</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Allocates every commission transaction on this policy across producers by % of carrier-paid commission. Replaces individual rate rules while effective.</p>
          <div className="space-y-3">
            <div><label className="text-xs text-slate-500">Policy</label>
              <Select value={sform.policy_id} onValueChange={(v) => setSform({ ...sform, policy_id: v })}>
                <SelectTrigger data-testid="cc-split-policy-select"><SelectValue placeholder="Select policy" /></SelectTrigger>
                <SelectContent>
                  {policies.map((p) => <SelectItem key={p.id} value={p.id}>{p.policy_number}{p.insured_name ? ` — ${p.insured_name}` : ""}</SelectItem>)}
                </SelectContent>
              </Select></div>
            {sform.components.map((c, i) => (
              <div key={i} className="flex items-end gap-2">
                <div className="flex-1"><label className="text-xs text-slate-500">Producer {i + 1}</label>
                  <Select value={c.producer_id} onValueChange={(v) => setComp(i, { producer_id: v })}>
                    <SelectTrigger data-testid={`cc-split-comp-producer-${i}`}><SelectValue placeholder="Producer" /></SelectTrigger>
                    <SelectContent>
                      {producers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name_raw}{p.user_name ? ` → ${p.user_name}` : " (unlinked)"}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div className="w-24"><label className="text-xs text-slate-500">%</label>
                  <Input placeholder="e.g. 60" value={c.percent} onChange={(e) => setComp(i, { percent: e.target.value })} data-testid={`cc-split-comp-percent-${i}`} /></div>
                {sform.components.length > 2 && (
                  <Button variant="ghost" size="sm" className="h-9 text-red-600 px-2" onClick={() => setSform({ ...sform, components: sform.components.filter((_, j) => j !== i) })} data-testid={`cc-split-comp-remove-${i}`}>✕</Button>
                )}
              </div>
            ))}
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" disabled={sform.components.length >= 10}
                onClick={() => setSform({ ...sform, components: [...sform.components, { producer_id: "", percent: "" }] })} data-testid="cc-split-add-comp">
                <Plus size={13} className="mr-1" /> Add producer
              </Button>
              <span className={`text-xs font-semibold ${splitTotal > 100 ? "text-amber-700" : "text-slate-500"}`} data-testid="cc-split-total">Total {splitTotal}%</span>
            </div>
            {splitTotal > 100 && (
              <label className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded p-2.5 text-amber-800 cursor-pointer" data-testid="cc-split-over100-warning">
                <input type="checkbox" className="mt-0.5" checked={sform.confirm_over_100}
                  onChange={(e) => setSform({ ...sform, confirm_over_100: e.target.checked })} data-testid="cc-split-over100-confirm" />
                <span>Total exceeds 100% of carrier commission — the excess is an agency-funded incentive. I confirm this over-100 allocation is explicitly approved.</span>
              </label>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Effective from</label>
                <Input type="date" value={sform.effective_from} onChange={(e) => setSform({ ...sform, effective_from: e.target.value })} data-testid="cc-split-from-input" /></div>
              <div><label className="text-xs text-slate-500">Effective to (optional)</label>
                <Input type="date" value={sform.effective_to} onChange={(e) => setSform({ ...sform, effective_to: e.target.value })} data-testid="cc-split-to-input" /></div>
            </div>
            <div><label className="text-xs text-slate-500">Approval note (required, min 5 chars)</label>
              <Textarea placeholder="Who approved this split and why" value={sform.note} onChange={(e) => setSform({ ...sform, note: e.target.value })} data-testid="cc-split-note-input" /></div>
          </div>
          <Button onClick={saveSplit} disabled={!splitValid} data-testid="cc-split-save">Create split</Button>
        </DialogContent>
      </Dialog>

      <RevokeDialog item={revokeItem} kind={revokeKind} onClose={() => setRevokeItem(null)} onDone={load} />
    </div>
  );
}
